import { useState, useEffect } from "react";
import axios from "axios";
import {
    HiOutlineMail,
    HiOutlineClock,
    HiOutlineUser,
    HiOutlineClipboardCopy,
    HiCheck
} from "react-icons/hi";

const ITEMS_PER_PAGE = 7;

export default function MailHistory({ baseUrl, formatDate }) {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Track which email just got copied
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            setError("");

            try {
                const resp = await axios.get(`${baseUrl}/email-history`);
                setEmails(resp.data.data || []);
            } catch (err) {
                console.error(err);
                setError("Failed to load message history");
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [baseUrl]);

    // Filtering
    const filteredEmails = emails.filter((email) => {
        const matchesSearch =
            email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.from_name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDate = filterDate
            ? new Date(email.sent_at).toISOString().slice(0, 10) === filterDate
            : true;

        return matchesSearch && matchesDate;
    });

    // Pagination
    const totalPages = Math.ceil(filteredEmails.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedEmails = filteredEmails.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterDate]);

    const copyHtml = async (email) => {
        try {
            let converted = email.html
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/\\n/g, "\n");

            converted = converted.replace(/<\/?[^>]+(>|$)/g, "");

            await navigator.clipboard.writeText(converted);
            setCopiedId(email.id);

            setTimeout(() => setCopiedId(null), 4000);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };


    if (loading) return <div className="p-5">Loading history...</div>;
    if (error) return <div className="p-5 text-red-600">{error}</div>;

    return (
        <div>
            <h2 className="text-lg font-semibold mb-3">Message History</h2>

            {/* Search & Date Filter */}
            <div className="flex flex-col md:flex-row gap-2 mb-4">
                <input
                    type="text"
                    placeholder="Search by subject or sender"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border rounded px-3 py-2 w-full md:w-1/2"
                />
                <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="border rounded px-3 py-2 w-full md:w-1/3"
                />
            </div>

            {filteredEmails.length === 0 ? (
                <p className="text-gray-600">No emails found.</p>
            ) : (
                <>
                    <div className="space-y-6">
                        {paginatedEmails.map((email) => (
                            <div key={email.id} className="border rounded-lg p-3 shadow-sm bg-white">
                                {/* Header */}
                                <div className="flex items-center gap-2">
                                    <HiOutlineMail className="text-indigo-600" />
                                    <h3 className="font-semibold text-lg">{email.subject}</h3>
                                </div>

                                {/* Sender + Date */}
                                <div className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                                    <HiOutlineUser />
                                    <span>{email.from_name}</span>

                                    <HiOutlineClock className="ml-4" />
                                    <span>{formatDate(email.sent_at)}</span>
                                </div>

                                <div className="flex items-center justify-between mb-3">
                                    {/* Total Sent */}
                                    <div className="text-sm text-gray-500">
                                        Sent to: <span className="font-semibold">{email.total_sent}</span>
                                    </div>

                                    {/* Copy Button */}
                                    <button
                                        onClick={() => copyHtml(email)}
                                        className="flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                                    >
                                        {copiedId === email.id ? (
                                            <>
                                                <HiCheck className="text-green-600 w-4 h-4" /> Copied!
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineClipboardCopy className="w-4 h-4" /> Copy
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* HTML Body */}
                                <div
                                    className="border p-4 rounded-lg prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: email.html }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-end items-center gap-2 mt-4">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded border ${currentPage === 1
                                ? "text-gray-400 border-gray-300"
                                : "text-indigo-600 border-indigo-600"
                                }`}
                        >
                            Previous
                        </button>

                        <span className="text-sm text-gray-600">
                            Page {currentPage} of {totalPages}
                        </span>

                        <button
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded border ${currentPage === totalPages
                                ? "text-gray-400 border-gray-300"
                                : "text-indigo-600 border-indigo-600"
                                }`}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
